import { extractErrorMessage } from '../../utils/apiError';

// Чистые хелперы форматирования/маппинга для Stage D Decode Optimization UI.
// Никакой бизнес-логики backend (recommendation, backoff, thresholds) — только
// человекочитаемое представление уже посчитанного backend-ответа.
// Источник маппингов: docs/decoding/decoding.txt, разделы 8.4, 9.2, 13.

export const MODE_LABELS = {
  auto: 'Автоматически',
  all: 'Все кадры',
  balanced: 'Сбалансированный',
  keyframes: 'Только ключевые кадры',
};

export const MODE_OPTIONS = [
  {
    value: 'auto',
    label: 'Автоматически (рекомендуется)',
    hint: 'Backend может выбрать ALL, BALANCED или KEYFRAMES по последнему probe и сам выполняет fallback/re-probe.',
  },
  {
    value: 'all',
    label: 'Все кадры',
    hint: 'Максимальная совместимость; legacy safe mode; экономия CPU отсутствует.',
  },
  {
    value: 'balanced',
    label: 'Сбалансированный',
    hint: 'Умеренное снижение decode workload; эффект зависит от codec/потока.',
  },
  {
    value: 'keyframes',
    label: 'Только ключевые кадры',
    hint: 'Максимальная экономия CPU; требует достаточного GOP/keyframe запаса.',
  },
];

export const modeLabel = (mode) => {
  if (mode === null || mode === undefined) return '—';
  if (Object.prototype.hasOwnProperty.call(MODE_LABELS, mode)) return MODE_LABELS[mode];
  return mode;
};

// Только для UI-предупреждения об агрессивном manual override — НЕ recommendation logic.
export const MODE_RANK = {
  all: 0,
  balanced: 1,
  keyframes: 2,
};

export const isMoreAggressive = (a, b) => {
  const rankA = MODE_RANK[a];
  const rankB = MODE_RANK[b];
  if (rankA === undefined || rankB === undefined) return false;
  return rankA > rankB;
};

export const formatCodec = (codec) => {
  if (codec === null || codec === undefined || codec === '') return '—';
  const normalized = String(codec).toLowerCase();
  if (normalized === 'h264') return 'H.264';
  if (normalized === 'hevc' || normalized === 'h265') return 'H.265 (HEVC)';
  return codec;
};

const isBlank = (v) => v === null || v === undefined || Number.isNaN(Number(v));

export const formatFps = (v) => {
  if (isBlank(v)) return '—';
  return Number(v).toFixed(1);
};

export const formatSeconds = (v) => {
  if (isBlank(v)) return '—';
  return `${Number(v).toFixed(2)} с`;
};

export const formatInteger = (v) => {
  if (isBlank(v)) return '—';
  return String(Math.round(Number(v)));
};

const DATE_TIME_OPTIONS = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

export const formatDateTime = (iso, timeZone) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  try {
    return new Intl.DateTimeFormat('ru-RU', { ...DATE_TIME_OPTIONS, timeZone }).format(date);
  } catch (err) {
    // Невалидная timezone — fallback на browser locale, компонент не должен падать.
    return new Intl.DateTimeFormat('ru-RU', DATE_TIME_OPTIONS).format(date);
  }
};

// Дословно из ТЗ §8.4.
export const RECOMMENDATION_REASON_TEXT = {
  not_probed: 'Поток ещё не проверен. Для безопасной рекомендации запустите проверку.',
  probe_missing_codec_or_fps: 'Не удалось получить codec/FPS. Рекомендуется ALL.',
  insufficient_keyframe_intervals: 'Недостаточно keyframe-интервалов для надёжной рекомендации. Рекомендуется ALL.',
  frame_interval_has_3x_keyframe_p95_margin: 'Есть безопасный запас >= 3 x keyframe p95. KEYFRAMES подходит.',
  frame_interval_has_2x_keyframe_p95_margin: 'Запас >= 2 x keyframe p95. Рекомендуется BALANCED.',
  frame_interval_below_2x_keyframe_p95_margin: 'Интервал слишком мал относительно keyframe p95. Рекомендуется ALL.',
};

export const recommendationReasonText = (code) => {
  if (code === null || code === undefined) return null;
  return RECOMMENDATION_REASON_TEXT[code] || null;
};

// Дословно из ТЗ §9.2.
export const FALLBACK_REASON_TEXT = {
  invalid_samples: 'Получено несколько невалидных кадров подряд.',
  no_valid_samples: 'В оптимизированном режиме не получены валидные кадры.',
  stale_valid_samples: 'Свежие валидные кадры перестали поступать вовремя.',
  optimized_initial_start_failed: 'Оптимизированный режим не удалось запустить при старте.',
  optimized_mode_start_failed: 'Не удалось запустить выбранный экономичный режим.',
  auto_reprobe_start_failed: 'Не удалось запустить автоматическую повторную проверку.',
  optimized_ffmpeg_decode_error: 'FFmpeg сообщил об ошибке декодирования в optimized mode.',
  optimized_ffmpeg_stream_closed: 'Поток закрылся при optimized mode; система вернулась в ALL.',
  optimized_ffmpeg_stderr_empty: 'Optimized FFmpeg завершился без диагностического сообщения.',
  optimized_ffmpeg_stderr_unclassified: 'Optimized FFmpeg завершился с нераспознанной диагностикой.',
};

// Неизвестная причина — общий текст; технический код показывается отдельно панелью
// (ТЗ §9.2: «не скрывать неизвестную причину полностью»).
const FALLBACK_REASON_FALLBACK_TEXT = 'Причина fallback не распознана.';

export const fallbackReasonText = (code) => {
  if (code === null || code === undefined) return FALLBACK_REASON_FALLBACK_TEXT;
  return FALLBACK_REASON_TEXT[code] || FALLBACK_REASON_FALLBACK_TEXT;
};

export const PROBE_ERROR_TEXT = {
  probe_timeout: 'Проверка потока не уложилась в отведённое время. Повторите позже или используйте ALL.',
  probe_unavailable: 'Проверка потока сейчас недоступна. Повторите позже или используйте ALL.',
  probe_failed: 'Проверка потока завершилась ошибкой. Повторите позже или используйте ALL.',
  probe_invalid_json: 'Не удалось разобрать результат проверки потока. Повторите позже или используйте ALL.',
  probe_missing_video_stream: 'В потоке не найден видеопоток. Повторите позже или используйте ALL.',
};

export const REPROBE_ERROR_TEXT = {
  all_mode_has_no_optimization_reprobe: 'Режим ALL не использует оптимизацию декодирования — повторная проверка не требуется.',
  auto_has_no_safe_optimized_recommendation: 'Пока нет безопасной рекомендации для оптимизированного режима.',
};

// Дословно из ТЗ §11.
export const apiErrorDetail = (error) =>
  extractErrorMessage(error, error?.message || 'Неизвестная ошибка');

export const decodeErrorMessage = (error, kind) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    return 'Недостаточно прав для управления оптимизацией.';
  }

  if (status === 404) {
    return 'Камера не найдена или удалена.';
  }

  if (status === 409) {
    return 'Настройка изменена в другой сессии. Данные обновлены; повторите действие.';
  }

  if (status === 422) {
    const detail = apiErrorDetail(error);
    if (kind === 'probe') return PROBE_ERROR_TEXT[detail] || detail;
    if (kind === 'reprobe') return REPROBE_ERROR_TEXT[detail] || detail;
    return detail;
  }

  if (!status || status >= 500) {
    return 'Не удалось загрузить данные. Повторите загрузку.';
  }

  return apiErrorDetail(error);
};
